import React, { useState } from 'react';
import { Button, Icon, Text } from '@upsales/components';
import Bem from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { useSelector, useSoftDeployAccess } from 'App/components/hooks';
import { useMetadata } from 'App/components/hooks/appHooks';
import history from 'App/pages/routes/history';
import useAiPromptsAccess from 'App/pages/Admin/subPages/AdminAIPrompts/useAiPromptsAccess';
import NavbarButton from '../NavbarButton';
import './NavbarAiOptIn.scss';

const storageKey = (customerId: number, userId: number) => `aiOptInBanner:dismissed:${customerId}-${userId}`;
const reacceptStorageKey = (termsId: number, customerId: number, userId: number) =>
	`aiOptInBanner:reacceptDismissed:${termsId}:${customerId}-${userId}`;

const isDismissed = (key: string) => {
	try {
		return localStorage.getItem(key) === 'true';
	} catch (error) {
		console.warn('Could not read whether the AI opt-in notice was dismissed', error);
		return false;
	}
};

const storeDismissed = (key: string) => {
	try {
		localStorage.setItem(key, 'true');
	} catch (error) {
		console.warn('Could not store that the AI opt-in notice was dismissed', error);
	}
};

const NavbarAiOptIn = () => {
	const { t } = useTranslation();
	const hasAccess = useSoftDeployAccess('AI_OPT_IN');
	// /alliwant sends aiAgreement only to an administrator, so no one else sees this.
	const aiAgreement = useSelector(({ App }) => App.aiAgreement);
	const customerId = useSelector(({ App }) => App.customerId);
	const userId = useSelector(({ App }) => App.self?.id);
	const isOn = useMetadata()?.params?.AiOutsideEuOptIn === true;
	const canOpenSettings = useAiPromptsAccess();
	const [dismissedKey, setDismissedKey] = useState<string | null>(null);

	if (!hasAccess || !aiAgreement || !canOpenSettings || !customerId || !userId) {
		return null;
	}

	const needsReaccept = isOn && aiAgreement.needsReaccept;
	if (isOn && !needsReaccept) {
		return null;
	}

	const key = needsReaccept
		? reacceptStorageKey(aiAgreement.termsId, customerId, userId)
		: storageKey(customerId, userId);
	if (dismissedKey === key || isDismissed(key)) {
		return null;
	}

	const classes = new Bem('NavbarAiOptIn');

	return (
		<NavbarButton
			className="navbar-ai-opt-in"
			title={t(needsReaccept ? 'aiAgreement.reaccept.banner' : 'aiOptIn.banner.text')}
			onClick={() => history.push('/admin/ai-prompts')}
		>
			<Icon name="exclamation-triangle" color="orange" />
			<Text size="sm" bold color="white" space="mls">
				{t(needsReaccept ? 'aiAgreement.reaccept.short' : 'aiOptIn.banner.short')}
			</Text>
			<Button
				className={classes.elem('dismiss').b()}
				color="white"
				type="link"
				title={t('default.dismiss')}
				aria-label={t('default.dismiss')}
				onClick={(event: React.MouseEvent) => {
					event.stopPropagation();
					storeDismissed(key);
					setDismissedKey(key);
				}}
			>
				<Icon name="times" />
			</Button>
		</NavbarButton>
	);
};

export default NavbarAiOptIn;
